import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import SEO from "@/components/seo/SEO";
import { Eyebrow, SectionHeading, AmbientOrbs } from "@/components/common/ui";
import { listArticles } from "@/lib/api";

export default function Knowledge() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language || "en";
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    listArticles(lang)
      .then((data) => {
        if (alive) setArticles(data);
      })
      .catch(() => {})
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [lang]);

  return (
    <>
      <SEO title={lang === "nl" ? "Knowledge Center" : "Knowledge Center"} path="/knowledge" />
      <section className="relative overflow-hidden py-32 lg:py-44">
        <AmbientOrbs />
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <Eyebrow>{t("knowledge.eyebrow")}</Eyebrow>
          <motion.h1
            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
            className="mt-8 max-w-4xl font-serif text-5xl leading-[1.02] tracking-tight sm:text-6xl md:text-7xl"
          >
            {t("knowledge.h1")}
          </motion.h1>
          <p className="mt-8 max-w-2xl text-lg text-white/65">{t("knowledge.sub")}</p>
        </div>
      </section>

      <section className="relative border-t border-white/[0.06] py-24">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          {loading ? (
            <div className="text-white/40">Loading…</div>
          ) : (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {articles.map((a, i) => (
                <motion.article
                  key={a.id}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.7, delay: i * 0.05, ease: [0.16, 1, 0.3, 1] }}
                  className="group"
                  data-testid={`knowledge-article-${a.slug}`}
                >
                  <Link to={`/${lang}/knowledge/${a.slug}`} className="block h-full rounded-2xl border border-white/[0.06] bg-white/[0.02] p-8 transition-colors hover:border-bht-gold/30">
                    <div className="flex items-center justify-between">
                      <span className="font-mono text-[10px] uppercase tracking-widest text-bht-gold">{a.category}</span>
                      <span className="font-mono text-[10px] tracking-widest text-white/30">{a.reading_time} {t("knowledge.reading_time")}</span>
                    </div>
                    <h2 className="mt-8 font-serif text-2xl leading-tight tracking-tight transition-colors group-hover:text-bht-gold">
                      {a.title}
                    </h2>
                    <p className="mt-4 text-sm leading-relaxed text-white/60">{a.excerpt}</p>
                    <div className="mt-8 flex items-center gap-2 text-[12px] text-white/50 transition-colors group-hover:text-bht-gold">
                      {t("knowledge.read_more")} <span className="transition-transform group-hover:translate-x-1">→</span>
                    </div>
                  </Link>
                </motion.article>
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );
}
